import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT_INFO } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentScreen = ({ data, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const [amountDue, setAmountDue] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(0);
  const [phonepeConfigured, setPhonepeConfigured] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const submitApplication = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(`${API}/vendor/apply`, data);
        setReferenceId(res.data.reference_id);
        setAmountDue(res.data.amount_due);
        setDiscountApplied(res.data.discount_applied || 0);
        const orderRes = await axios.post(`${API}/vendor/create-order`, { reference_id: res.data.reference_id });
        setPhonepeConfigured(orderRes.data.configured);
        window.__tmp_order = orderRes.data;
      } catch (err) {
        setError(err?.response?.data?.detail || "Something went wrong submitting your application.");
        toast.error("Something went wrong submitting your application.");
      } finally {
        setLoading(false);
      }
    };
    submitApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhonePePay = () => {
    const order = window.__tmp_order;
    if (!order?.redirect_url) {
      toast.error("Payment link unavailable. Please try again.");
      return;
    }
    setRedirecting(true);
    window.location.href = order.redirect_url;
  };

  const handleManualConfirm = async () => {
    setRedirecting(true);
    try {
      await axios.post(`${API}/vendor/manual-confirm`, { reference_id: referenceId });
      window.location.href = `${window.location.origin}/vendor-booking?order_id=${referenceId}`;
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="screen-4-payment-loading" className="text-center py-20">
        <p className="text-gray-400 font-mono text-sm">Preparing your application...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="screen-4-payment-error" className="max-w-xl mx-auto text-center py-16">
        <p className="text-red-500 mb-6">{error}</p>
        <button onClick={onBack} data-testid="payment-error-back-button" className="rounded-full border border-gray-300 px-6 py-3 text-sm">Back to details</button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="screen-4-payment" className="max-w-xl mx-auto text-center">
      <h2 className="font-extrabold text-3xl sm:text-4xl mb-2">Confirm and pay</h2>
      <p className="text-gray-500 mb-8">Reference ID: <span className="font-mono text-[#D81B60]" data-testid="payment-reference-id">{referenceId}</span></p>

      <div className="rounded-2xl bg-[#1A1A1A] text-white p-8 mb-8 text-left">
        <p className="text-sm uppercase tracking-[0.15em] text-white/50 font-mono">Amount Due</p>
        <p className="text-4xl mt-2 text-[#F48FB1]" data-testid="payment-amount-due">₹{amountDue?.toLocaleString("en-IN")}</p>
        {discountApplied > 0 && (
          <p className="text-sm text-[#F48FB1] font-mono mt-1" data-testid="payment-discount-applied">
            Bulk discount applied: −₹{discountApplied.toLocaleString("en-IN")}
          </p>
        )}
        {(data.stall_selections || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5">
            {data.stall_selections.map((s) => (
              <p key={s.date} className="text-sm text-white/60 flex justify-between" data-testid={`payment-summary-${s.date}`}>
                <span>{s.date} — Stall #{s.stall_number}{s.electricity ? " · Electrical Connection" : ""}{s.extra_table ? " · Extra Table" : ""}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      {phonepeConfigured ? (
        <button
          onClick={handlePhonePePay}
          disabled={redirecting}
          data-testid="pay-with-phonepe-button"
          className="w-full rounded-full bg-[#D81B60] text-white py-4 text-sm hover:bg-[#AD144B] transition-colors duration-300 disabled:opacity-60"
        >
          {redirecting ? "Redirecting to PhonePe..." : "Pay with PhonePe"}
        </button>
      ) : (
        <div data-testid="manual-payment-fallback" className="rounded-2xl border border-gray-200 p-6 text-left">
          <p className="text-sm text-gray-600 mb-4">
            Online payment is being set up. Please complete payment via UPI/bank transfer using the details below,
            or reach out directly, and our team will confirm once received.
          </p>
          <p className="text-sm font-mono text-[#1A1A1A]">Email: {CONTACT_INFO.email}</p>
          <p className="text-sm font-mono text-[#1A1A1A] mb-4">Phone: {CONTACT_INFO.phone}</p>
          <button
            onClick={handleManualConfirm}
            disabled={redirecting}
            data-testid="manual-confirm-submit-button"
            className="w-full rounded-full bg-[#D81B60] text-white py-4 text-sm hover:bg-[#AD144B] transition-colors duration-300 disabled:opacity-60"
          >
            {redirecting ? "Submitting..." : "Submit Application"}
          </button>
        </div>
      )}

      <button onClick={onBack} data-testid="payment-back-button" className="mt-6 text-sm text-gray-400 underline">
        Back to details
      </button>
    </motion.div>
  );
};
